import type { ImageCrop } from '@bazaar/shared';

export interface CropPreviewProps {
  /** The full (uncropped) image -- a local object URL for a freshly-picked file, or the product's originalUrl. */
  src: string;
  crop: ImageCrop;
  size?: number;
  alt?: string;
}

/**
 * Renders exactly the square region `crop` (fractions 0..1 of `src`'s
 * natural width/height) of `src`, scaled to a `size`x`size` box -- without
 * uploading anything. Used to preview a freshly-picked photo's chosen crop
 * before Save, matching what the server will render.
 */
export function CropPreview({ src, crop, size = 160, alt = '' }: CropPreviewProps) {
  const bgWidth = crop.w > 0 ? size / crop.w : size;
  const bgHeight = crop.h > 0 ? size / crop.h : size;
  const posX = crop.w > 0 ? -(crop.x / crop.w) * size : 0;
  const posY = crop.h > 0 ? -(crop.y / crop.h) * size : 0;

  return (
    <div
      role="img"
      aria-label={alt}
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${src})`,
        backgroundSize: `${bgWidth}px ${bgHeight}px`,
        backgroundPosition: `${posX}px ${posY}px`,
        backgroundRepeat: 'no-repeat',
      }}
      className="rounded-lg overflow-hidden bg-teal-dark"
    />
  );
}
